import { readBetGameDashboard } from '@/api/bet/read';
import TotalUsers from '@/component/bet/dashboard/TotalUsers';
import { useEffect, useState } from 'react';
import TotalCount from '@/component/bet/dashboard/TotalCount';
import { Box, Button, Card, Divider, Typography } from '@mui/material';
import TotalAmount from '@/component/bet/dashboard/TotalAmount';
import CostSmall from '@/component/bet/dashboard/CostSmall';
import WinStatus from '@/component/bet/dashboard/WinStatus';
import SwapToToken from '@/component/bet/dashboard/SwapToToken';
import SwapToPoint from '@/component/bet/dashboard/SwapToPoint';
import MismatchData from '@/component/bet/dashboard/MismatchData';
import GameSetting from '@/component/bet/dashboard/GameSetting';
import { Tables } from '@/component/pools/table/Tables';
import {
  betUserListColumns,
  smallWinListColumns,
  SwapListColumns,
  winUserListColumns,
  createBetUserListColumns,
  referralDataColumns,
  referralListColumns,
  rankingListColumns,
  jackpotListColumns,
} from '@/component/bet/table/DashboardData';
import { useNavigate } from 'react-router-dom';
import Header from '@/component/Header';
import ReferralData from '@/component/bet/dashboard/ReferralData';
import { betStatusFilter } from '@/lib/filter/bet';
import { packBigNumbers } from '@/lib/BigNumbers';

const ButtonName = {
  BET_USER_LIST: '참여자 리스트',
  WIN_USER_LIST: '당첨자 리스트',
  SMALL_WIN_USER_LIST: '소액 당첨자 리스트',
  SWAP_TO_TOKEN_LIST: 'TO TOKEN 리스트',
  SWAP_TO_POINT_LIST: 'TO POINT 리스트',
  REFERRAL_LIST: '레퍼럴 리스트',
  RANKING_LIST: '랭킹 리스트',
  JACKPOT_LIST: '잭팟 당첨 리스트',
};
type ProcessResult = {
  list: any;
  columns?: any;
};

export default function BetGameDashboard() {
  const [data, setData] = useState<any | null>(null);
  const [selectedButton, setSelectedButton] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);

  const [selectData, setSelectData] = useState<any | null>({
    selectedList: null,
    selectedListColumns: null,
    selectedListRows: null,
    selectedGetRowId: null,
  });
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const re = await readBetGameDashboard();
      console.log(re);
      setData(re.data);
    })();

    return () => {};
  }, []);

  useEffect(() => {
    if (selectedButton === ButtonName.BET_USER_LIST && selectedRow) {
      navigate('/bet-user-ledger-list', {
        state: {
          address: selectedRow.address,
        },
      });
    }
  }, [selectedButton, selectedRow]);

  // 참여자 리스트 처리 함수
  const processBetUserList = (list: any, columns: any) => {
    const finalColumns = createBetUserListColumns(list);
    const finalList = list.map((item: any) => {
      const formattedItem: any = { ...item };
      if (formattedItem.total !== undefined) {
        formattedItem.total = packBigNumbers(formattedItem.total || '0');
      }
      if (formattedItem.total_count !== undefined) {
        formattedItem.total_count = packBigNumbers(formattedItem.total_count || '0');
      }
      // 동적 게임 필드들 포맷팅
      Object.keys(formattedItem).forEach((key) => {
        if (
          key !== 'address' &&
          key !== 'total' &&
          key !== 'total_count' &&
          key !== 'referral_code' &&
          formattedItem[key] !== undefined
        ) {
          formattedItem[key] = packBigNumbers(formattedItem[key] || '0');
        }
      });
      return formattedItem;
    });
    return { finalList, finalColumns };
  };
  // 당첨자 리스트 처리 함수
  const processWinUserList = (list: any, columns: any) => {
    const finalColumns = columns;
    const finalList = list.map((item: any) => ({
      ...item,
      bet_amount: packBigNumbers(item.bet_amount),
      win_amount: packBigNumbers(item.win_amount),
    }));
    return { finalList, finalColumns };
  };
  // 소액 당첨자 리스트 처리 함수
  const processSmallWinUserList = (list: any, columns: any) => {
    const finalColumns = columns;
    const finalList = list.map((item: any) => ({
      ...item,
      amount: packBigNumbers(item.amount),
    }));
    return { finalList, finalColumns };
  };
  // TO TOKEN 리스트 처리 함수
  const processSwapToTokenList = (list: any, columns: any) => {
    const finalColumns = columns;
    const finalList = list.map((item: any) => ({
      ...item,
      bet_amount: packBigNumbers(item.bet_amount),
      token_amount: packBigNumbers(item.token_amount),
    }));
    return { finalList, finalColumns };
  };
  // TO POINT 리스트 처리 함수
  const processSwapToPointList = (list: any, columns: any) => {
    const finalColumns = columns;
    const finalList = list.map((item: any) => ({
      ...item,
      bet_amount: packBigNumbers(item.bet_amount),
      token_amount: packBigNumbers(item.token_amount),
    }));
    return { finalList, finalColumns };
  };
  // 레퍼럴 리스트 처리 함수
  const processReferralList = (list: any, columns: any) => {
    const finalColumns = columns;
    const finalList = list.map((item: any) => ({
      ...item,
      amount: packBigNumbers(item.amount),
      status: betStatusFilter(item.status),
      // referral_bet_amount: packBigNumbers(item.referral_bet_amount),
      // referral_bet_count: packBigNumbers(item.referral_bet_count),
      // referral_win_amount: packBigNumbers(item.referral_win_amount),
      // referral_win_count: packBigNumbers(item.referral_win_count),
      // referral_small_win_amount: packBigNumbers(item.referral_small_win_amount),
      // referral_small_win_count: packBigNumbers(item.referral_small_win_count),
      // total_amount: packBigNumbers(item.total_amount),
      // total_count: packBigNumbers(item.total_count),
    }));
    return { finalList, finalColumns };
  };

  // 랭킹 리스트 처리 함수
  const processRankingList = (list: any, columns: any) => {
    const finalColumns = columns;
    const finalList = list.map((item: any) => ({
      ...item,
      total_amount: packBigNumbers(item.total_amount),
      win_count: packBigNumbers(item.win_count),
      small_win_count: packBigNumbers(item.small_win_count),
      jackpot_count: packBigNumbers(item.jackpot_count),
    }));
    return { finalList, finalColumns };
  };

  // 잭팟 리스트 처리 함수
  const processJackpotList = (list: any, columns: any) => {
    const finalColumns = columns;
    const finalList = list.map((item: any) => ({
      ...item,
      amount: packBigNumbers(item.amount),
    }));
    return { finalList, finalColumns };
  };

  const listProcessors: Record<string, (list: any, columns: any) => ProcessResult> = {
    [ButtonName.BET_USER_LIST]: (list, columns) => {
      // 참여자 리스트
      const result = processBetUserList(list, columns);
      return { list: result.finalList, columns: result.finalColumns };
    },
    [ButtonName.WIN_USER_LIST]: (list, columns) => {
      // 당첨자 리스트
      const result = processWinUserList(list, columns);
      return { list: result.finalList, columns: result.finalColumns };
    },
    [ButtonName.SMALL_WIN_USER_LIST]: (list, columns) => {
      // 소액 당첨자 리스트
      const result = processSmallWinUserList(list, columns);
      return { list: result.finalList, columns: result.finalColumns };
    },
    [ButtonName.SWAP_TO_TOKEN_LIST]: (list, columns) => {
      // TO TOKEN 리스트
      const result = processSwapToTokenList(list, columns);
      return { list: result.finalList, columns: result.finalColumns };
    },
    [ButtonName.SWAP_TO_POINT_LIST]: (list, columns) => {
      // TO POINT 리스트
      const result = processSwapToPointList(list, columns);
      return { list: result.finalList, columns: result.finalColumns };
    },
    [ButtonName.REFERRAL_LIST]: (list, columns) => {
      // 레퍼럴 리스트
      const result = processReferralList(list, columns);
      return { list: result.finalList, columns: result.finalColumns };
    },
    [ButtonName.RANKING_LIST]: (list, columns) => {
      // 일일 랭킹
      const result = processRankingList(list, columns);
      return { list: result.finalList, columns: result.finalColumns };
    },
    [ButtonName.JACKPOT_LIST]: (list, columns) => {
      // 잭팟 리스트
      const result = processJackpotList(list, columns);
      return { list: result.finalList, columns: result.finalColumns };
    },
  };

  const handleButtonClick = (buttonName: string, list: any, columns: any, getRowId: (row: any) => string) => {
    let finalColumns = columns;
    let finalList = list;

    if (list && listProcessors[buttonName]) {
      const { list: processedList, columns: processedColumns } = listProcessors[buttonName](list, columns);
      finalList = processedList;
      if (processedColumns) {
        finalColumns = processedColumns;
      }
    }

    setSelectedButton(buttonName);
    setSelectData({
      selectedList: finalList,
      selectedListColumns: finalColumns,
      selectedListRows: null,
      selectedGetRowId: getRowId,
    });
    setSelectedRow(null); // 새로운 리스트 선택 시 선택된 행 초기화
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          overflow: 'auto',
          flex: 1,
          flexDirection: 'column',
          gap: { xs: '16px', sm: '0px' },
          height: '100vh',
          maxHeight: '100vh',
        }}
      >
        <Card>
          <Header title="세팅 리스트" />
          <Divider />
          <Box mb={2} />

          <Box sx={{ p: 3, maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <Box sx={{ mb: 2 }}>BetGameDashboard</Box>
            {data && (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <TotalUsers datas={data.totalUsers} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TotalCount datas={data.totalCount} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <TotalAmount datas={data.totalAmount} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <CostSmall datas={data.costSmallAmount} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <GameSetting datas={data.gameSetting} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <WinStatus datas={data.winStatus} />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <SwapToToken datas={data.swapToToken} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <SwapToPoint datas={data.swapToPoint} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, width: '100%' }}>
                  <Box sx={{ flex: 1, width: '100%' }}>
                    <ReferralData datas={data.referralData} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, width: '100%' }}>
                  <Box sx={{ flex: 1, width: '100%' }}>
                    <MismatchData datas={data.mismatchData} />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      handleButtonClick(
                        ButtonName.BET_USER_LIST,
                        data.betUserList,
                        betUserListColumns,
                        (row) => row.address,
                      )
                    }
                  >
                    참여자 리스트
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      handleButtonClick(
                        ButtonName.WIN_USER_LIST,
                        data.winList,
                        winUserListColumns,
                        (row) => row.history_id,
                      )
                    }
                  >
                    당첨자 리스트
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      handleButtonClick(
                        ButtonName.SMALL_WIN_USER_LIST,
                        data.smallWinList,
                        smallWinListColumns,
                        (row) => row.bet_id,
                      )
                    }
                  >
                    소액 당첨자 리스트
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      handleButtonClick(
                        ButtonName.SWAP_TO_TOKEN_LIST,
                        data.swapToTokenList,
                        SwapListColumns,
                        (row) => row.swap_id,
                      )
                    }
                  >
                    TO TOKEN 리스트
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      handleButtonClick(
                        ButtonName.SWAP_TO_POINT_LIST,
                        data.swapToPointList,
                        SwapListColumns,
                        (row) => row.swap_id,
                      )
                    }
                  >
                    TO POINT 리스트
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      handleButtonClick(
                        ButtonName.REFERRAL_LIST,
                        data.referralList,
                        referralListColumns,
                        (row) => row.bet_id,
                      )
                    }
                  >
                    레퍼럴 리스트
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      handleButtonClick(
                        ButtonName.RANKING_LIST,
                        data.dailyRank?.rankings,
                        rankingListColumns,
                        (row) => row.rank,
                      )
                    }
                  >
                    일일 랭킹({data.dailyRank?.date ?? ''})
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      handleButtonClick(
                        ButtonName.JACKPOT_LIST,
                        data.jackpotWinList,
                        jackpotListColumns,
                        (row) => row.jackpot_id,
                      )
                    }
                  >
                    잭팟 당첨 리스트
                  </Button>
                </Box>
                {selectedButton && (
                  <Box sx={{ mt: 3, p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {selectedButton}
                    </Typography>
                    <Tables
                      list={selectData.selectedList}
                      columns={selectData.selectedListColumns}
                      getRowId={selectData.selectedGetRowId}
                      // navigateUrl={getNavigateUrl()}
                      onRowSelect={setSelectedRow}
                    />
                    {/* {selectedButton === ButtonName.BET_USER_LIST && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRow ? `선택된 지갑주소: ${selectedRow.address}` : '테이블에서 행을 선택해주세요'}
                  </Typography>
                </Box>
              )} */}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Card>
      </Box>
    </>
  );
}
